import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import { toast } from "react-toastify";
import { loaderDecrement, loaderIncrement } from "../services/globalLoader";

const pickTenant = (data) => {
  if (!data || typeof data !== "object") return null;
  if (data.tenant && typeof data.tenant === "object") return data.tenant;
  if (data.result && typeof data.result === "object") return data.result;
  if (data._id || data.id || data.userid || data.email) {
    const { status, message, ...rest } = data;
    return Object.keys(rest).length ? rest : data;
  }
  return null;
};

const formatInr = (n) =>
  new Intl.NumberFormat("en-IN", {
    style: "currency",
    currency: "INR",
    maximumFractionDigits: 0
  }).format(Number(n || 0));

const formatMoneyLabel = (v) => {
  if (v == null || v === "") return null;
  const num = Number(v);
  if (Number.isNaN(num)) return null;
  return formatInr(num);
};

const formatDateTime = (iso) => {
  if (iso == null || iso === "") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" });
};

const formatDate = (iso) => {
  if (iso == null || iso === "") return null;
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return String(iso);
  return d.toLocaleDateString("en-IN", { dateStyle: "medium" });
};

const relationshipLabel = (value) => {
  const map = {
    spouse: "Spouse",
    son: "Son",
    daughter: "Daughter",
    brother: "Brother",
    sister: "Sister",
    father: "Father",
    mother: "Mother"
  };
  const key = String(value || "").trim().toLowerCase();
  return map[key] || value;
};

const DetailRow = ({ label, value }) => (
  <div className="row py-2 border-bottom" style={{ margin: 0 }}>
    <div className="col-12 col-sm-4 text-muted" style={{ fontSize: 13, fontWeight: 600 }}>
      {label}
    </div>
    <div className="col-12 col-sm-8" style={{ fontSize: 14 }}>
      {value != null && value !== "" ? String(value) : "—"}
    </div>
  </div>
);

const SectionTitle = ({ children, first }) => (
  <div
    className="text-muted text-uppercase"
    style={{
      fontSize: 11,
      fontWeight: 700,
      letterSpacing: "0.06em",
      marginTop: first ? 0 : 18,
      marginBottom: 8
    }}
  >
    {children}
  </div>
);

const TenantreportView = () => {
  const API_BASE_URL = process.env.REACT_APP_API_URL;
  const navigate = useNavigate();
  const { tenantId: tenantIdParam } = useParams();
  const tenantId = useMemo(() => {
    try {
      return tenantIdParam ? decodeURIComponent(String(tenantIdParam)).trim() : "";
    } catch {
      return String(tenantIdParam || "").trim();
    }
  }, [tenantIdParam]);

  const userid = useMemo(() => {
    try {
      return String(localStorage.getItem("userid") || "").trim();
    } catch {
      return "";
    }
  }, []);

    const isOwner = useMemo(() => {
    try {
      return String(localStorage.getItem("role") || "").toUpperCase().replace(/[^A-Z]/g, "") === "OWNER";
    } catch {
      return false;
    }
  }, []);

  const [loading, setLoading] = useState(true);
  const [tenant, setTenant] = useState(null);

  useEffect(() => {
    if (!tenantId) {
      toast.error("Invalid tenant link.");
      navigate("/tenant-report", { replace: true });
    }
  }, [tenantId, navigate]);

  const fetchTenant = useCallback(async () => {
    if (!API_BASE_URL || !tenantId) {
      if (!API_BASE_URL) toast.error("Missing REACT_APP_API_URL in environment");
      setLoading(false);
      return;
    }
    if (!userid) {
      toast.error("Missing userid. Please login again.");
      setLoading(false);
      return;
    }

    let didIncrement = false;
    try {
      loaderIncrement();
      didIncrement = true;
     const response = await axios.post(`${API_BASE_URL}`, {
        action: isOwner ? "owner.getTenant" : "admin.getTenant",
        userid,
        tenantId
      });
      const data = response?.data;

      if (data?.success === true || data?.status === "success") {
        const t = pickTenant(data);
        if (t) {
          setTenant(t);
        } else {
          toast.error(data?.message || "Tenant data not found in response");
          setTenant(null);
        }
      } else {
        toast.error(data?.message || "Failed to load tenant");
        setTenant(null);
      }
    } catch (e) {
      toast.error(e?.response?.data?.message || e?.message || "Something went wrong");
      setTenant(null);
    } finally {
      if (didIncrement) loaderDecrement();
      setLoading(false);
    }
  }, [API_BASE_URL, tenantId, userid,isOwner]);

  useEffect(() => {
    if (!tenantId) return;
    fetchTenant();
  }, [tenantId, fetchTenant]);

  if (!tenantId) {
    return null;
  }

  return (
    <div className="employee-form tenant-report">
      <div className="content-header">
        <div className="container-fluid">
          <div className="d-flex justify-content-between align-items-center flex-wrap" style={{ gap: 12 }}>
            <div>
              {/* <nav className="text-muted mb-1" style={{ fontSize: 13 }}>
                <Link to="/tenant-report">Tenant report</Link>
                <span className="mx-2">/</span>
                <span>View</span>
              </nav> */}
              <h1 className="m-0 txt_lft">Tenant details</h1>
              <p style={{ margin: 0, color: "#6c757d", fontSize: "14px" }}>
                Review owner, property, and tenancy details
              </p>
            </div>
            <div className="d-flex gap-2">
              
              <Link to="/tenant-report" className="btn btn-outline-secondary">
                Back to list
              </Link>
              {/* <button
                type="button"
                className="btn btn-secondary"
                disabled={loading}
                onClick={() => {
                  setLoading(true);
                  fetchTenant();
                }}
              >
                {loading ? "Loading…" : "Refresh"}
              </button> */}
            </div>
          </div>
        </div>
      </div>

      <div className="content">
        <div className="container-fluid">
          {loading && !tenant ? (
            <div className="card">
              <div className="card-body text-muted">Loading tenant…</div>
            </div>
          ) : tenant ? (
            <div className="card">
              <div className="card-body">
                <div className="mb-3">
                  <h2 className="h4 mb-1">{tenant?.name || "Tenant"}</h2>
                </div>

                <div>
                              {(() => {
                  const isTenantRecord = String(tenant?.role || "").toUpperCase() === "TENANT";
                  const p = isTenantRecord ? tenant?.ownerProperty : tenant;

                  return (
                    <div>
                      {isTenantRecord ? (
                        <>
                          <SectionTitle first>Tenant details</SectionTitle>
                          <div className="border rounded overflow-hidden mb-0">
                            <DetailRow label="Name" value={tenant?.name} />
                            <DetailRow label="Email" value={tenant?.email} />
                            <DetailRow label="Mobile" value={tenant?.phone} />
                            <DetailRow label="Registered" value={tenant?.isVerified ? "Yes" : "No"} />
                            <DetailRow label="Status" value={tenant?.isActive ? "Active" : "Inactive"} />
                          </div>
                        </>
                      ) : null}

                      {!p ? (
                        <div className="text-muted mt-3">Owner property details not available.</div>
                      ) : (
                        <>
                          <SectionTitle first={!isTenantRecord}>
                            {isTenantRecord ? "Owner" : "Primary owner"}
                          </SectionTitle>
                          <div className="border rounded overflow-hidden mb-0">
                            <DetailRow label="Name" value={p.name} />
                            <DetailRow label="Email" value={p.email} />
                            <DetailRow label="Mobile" value={p.phone} />
                            <DetailRow label="Alternative mobile" value={p.primaryOwnerAlternativeMobile} />
                            <DetailRow label="WhatsApp" value={p.primaryOwnerWhatsapp} />
                            <DetailRow label="Property status" value={p.propertyInfo} />
                          </div>

                          <SectionTitle>Property</SectionTitle>
                          <div className="border rounded overflow-hidden mb-0">
                            <DetailRow label="Block" value={p.block} />
                            <DetailRow label="Flat no." value={p.flatNumber} />
                            <DetailRow label="Unit" value={tenant?.unit?.unitNumber} />
                            <DetailRow label="BHK" value={p.bhkType} />
                            <DetailRow label="Sq.ft" value={p.squareFeet} />
                            <DetailRow label="Property purchase date" value={formatDate(p.dateOfPropertyPurchase)} />
                            <DetailRow label="Date of occupancy" value={formatDate(p.dateOfOccupancy)} />
                            <DetailRow label="Property type" value={p.propertyType} />
                            <DetailRow label="Property nature" value={p.propertyNature} />
                          </div>

                          <SectionTitle>Corpus and maintenance</SectionTitle>
                          <div className="border rounded overflow-hidden mb-0">
                            <DetailRow label="Corpus fund amount" value={formatMoneyLabel(p.corpusFundAmount)} />
                            <DetailRow label="Corpus fund paid date" value={formatDate(p.corpusFundPaidDate)} />
                            <DetailRow label="Monthly maintenance amount" value={formatMoneyLabel(p.monthlyMaintenanceAmount)} />
                          </div>

                          <SectionTitle>Joint owner</SectionTitle>
                          <div className="border rounded overflow-hidden mb-0">
                            <DetailRow label="Joint owner name" value={p.jointOwnerName} />
                            <DetailRow label="Relationship with primary owner" value={relationshipLabel(p.jointOwnerRelationship)} />
                            <DetailRow label="Joint owner's email" value={p.jointOwnerEmail} />
                            <DetailRow label="Joint owner's mobile" value={p.jointOwnerMobile} />
                            <DetailRow label="Joint owner's WhatsApp" value={p.jointOwnerWhatsapp} />
                          </div>

                          <SectionTitle>Let-out details</SectionTitle>
                          <div className="border rounded overflow-hidden mb-0">
                            <DetailRow label="Let out portion" value={p.letOutPortion} />
                            <DetailRow label="Type of tenancy" value={p.typeOfTenancy} />
                            <DetailRow label="Lease period" value={p.leasePeriod} />
                            <DetailRow label="Tenant name" value={p.tenantName} />
                            <DetailRow label="Tenant email" value={p.tenantEmail} />
                            <DetailRow label="Tenant phone" value={p.tenantPhoneNumber} />
                            <DetailRow label="Tenancy starting date" value={formatDate(p.tenancyStartingDate)} />
                            <DetailRow label="Advance amount" value={formatMoneyLabel(p.advanceAmount)} />
                            <DetailRow label="Monthly rent" value={formatMoneyLabel(p.monthlyRent)} />
                            <DetailRow label="Monthly maintenance charges" value={p.monthlyMaintenanceChargesBy} />
                          </div>
                        </>
                      )}

                      <SectionTitle>Record</SectionTitle>
                      <div className="border rounded overflow-hidden mb-0">
                        <DetailRow label="Created at" value={formatDateTime(tenant?.createdAt)} />
                        <DetailRow label="Updated at" value={formatDateTime(tenant?.updatedAt)} />
                      </div>
                    </div>
                  );
                })()}
                </div>
              </div>
            </div>
          ) : (
            <div className="card">
              <div className="card-body text-muted">
                Could not load this tenant.&nbsp;
                <Link to="/tenant-report">Return to list</Link>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default TenantreportView;
